import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, Upload, X } from 'lucide-react';

export default function TicketNewModal({ users, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('General');
  const [priority, setPriority] = useState('medium');
  const [assignedTo, setAssignedTo] = useState([]);
  const [dueDate, setDueDate] = useState('');
  const [location, setLocation] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/ticket-categories');
        setCategories(data || []);
      } catch (_) {}
    })();
  }, []);

  const groupedUsers = useMemo(() => {
    const g = { admin: [], staff: [], field_team: [] };
    (users || []).forEach((u) => {
      if (g[u.role]) g[u.role].push(u);
    });
    return g;
  }, [users]);

  const toggleAssignee = (uid) => {
    setAssignedTo((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // Upload to a "draft" endpoint would be nice, but simplest: upload after
      // create. For now we do a two-phase: create ticket first with 0 attachments,
      // then upload. We use a different approach: store the file locally and
      // upload post-create.
      setAttachments((prev) => [...prev, { file, previewUrl: URL.createObjectURL(file) }]);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const addCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      const { data } = await api.post('/api/ticket-categories', { name });
      setCategories((prev) => {
        if (prev.find((c) => c.name.toLowerCase() === data.name.toLowerCase())) return prev;
        return [...prev, data].sort((a, b) => a.name.localeCompare(b.name));
      });
      setCategory(data.name);
      setAddingCategory(false);
      setNewCategoryName('');
      toast.success('Category added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add category');
    }
  };

  const submit = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (assignedTo.length === 0) {
      toast.error('Assign the ticket to at least one person');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        description,
        category,
        priority,
        assigned_to: assignedTo,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
        location: location || null,
        attachments: [],
      };
      const { data: ticket } = await api.post('/api/tickets', payload);

      // Now upload any staged attachments
      for (const att of attachments) {
        const fd = new FormData();
        fd.append('file', att.file);
        try {
          await api.post(`/api/tickets/${ticket.id}/attachments`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (upErr) {
          console.error('attachment upload failed', upErr);
        }
      }
      toast.success(`Ticket ${ticket.ticket_no} created`);
      onCreated && onCreated(ticket);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="ticket-new-modal">
        <DialogHeader>
          <DialogTitle>New ticket</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What needs to be done?"
              data-testid="new-title"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Extra context or steps"
              data-testid="new-description"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              {addingCategory ? (
                <div className="flex gap-2">
                  <Input
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="New category name"
                    data-testid="new-category-name"
                  />
                  <Button type="button" onClick={addCategory} data-testid="save-category">
                    Save
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setAddingCategory(false)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="new-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAddingCategory(true)}
                    data-testid="add-category-btn"
                  >
                    <Plus size={14} />
                  </Button>
                </div>
              )}
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="new-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due date</Label>
              <Input
                type="datetime-local"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                data-testid="new-due-date"
              />
            </div>
            <div>
              <Label>Location (optional)</Label>
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. Baner branch"
                data-testid="new-location"
              />
            </div>
          </div>

          <div>
            <Label>Assign to *</Label>
            <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-3">
              {['admin', 'staff', 'field_team'].map((r) => (
                <div key={r}>
                  {groupedUsers[r].length > 0 && (
                    <>
                      <div className="text-xs uppercase font-semibold text-gray-500 mb-1">
                        {r === 'field_team' ? 'Field team' : r}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1">
                        {groupedUsers[r].map((u) => (
                          <label
                            key={u.id}
                            className="flex items-center gap-2 text-sm cursor-pointer"
                            data-testid={`assignee-option-${u.id}`}
                          >
                            <Checkbox
                              checked={assignedTo.includes(u.id)}
                              onCheckedChange={() => toggleAssignee(u.id)}
                            />
                            <span>{u.name || u.email}</span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            {assignedTo.length > 0 && (
              <div className="text-xs text-gray-600 mt-1">
                {assignedTo.length} user(s) selected
              </div>
            )}
          </div>

          <div>
            <Label>Attachments (optional)</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {attachments.map((a, i) => (
                <div key={i} className="relative">
                  <img
                    src={a.previewUrl}
                    alt=""
                    className="w-16 h-16 object-cover rounded-md border"
                  />
                  <button
                    type="button"
                    onClick={() => removeAttachment(i)}
                    className="absolute -top-1 -right-1 bg-white border rounded-full p-0.5"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <label className="w-16 h-16 border-2 border-dashed rounded-md flex items-center justify-center text-gray-400 cursor-pointer hover:bg-gray-50">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleUpload}
                  data-testid="new-attachment-input"
                />
                {uploading ? <span className="text-xs">…</span> : <Upload size={16} />}
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="new-submit">
            {submitting ? 'Creating…' : 'Create ticket'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
